import { Component, ElementRef, Input, ViewChild } from '@angular/core';

export interface EmdTooltipRow {
  label: string;
  value: string;
  color?: string;
  /** Period-over-period direction, drawn as the original's green/red arrow. */
  movement?: 'up' | 'down' | null;
}

/**
 * The one tooltip every EMD chart uses.
 *
 * Positioned against the chart host rather than the page, and flipped away from
 * whichever edge it would otherwise overflow — the shared charts each hand-rolled
 * this and each clipped differently near a container boundary.
 */
@Component({
    selector: 'emd-chart-tooltip',
    template: `
    <div
      #box
      class="emd-tip"
      role="tooltip"
      [class.is-visible]="visible"
      [class.is-light]="variant === 'light'"
      [style.left.px]="left"
      [style.top.px]="top"
    >
      <div class="emd-tip-title" *ngIf="title">{{ title }}</div>
      <div class="emd-tip-body">
        <div class="emd-tip-row" *ngFor="let row of rows">
          <span class="emd-tip-dot" *ngIf="row.color" [style.background]="row.color"></span>
          <span class="emd-tip-label">{{ row.label }}<ng-container *ngIf="row.value"> : {{ row.value }}</ng-container></span>
          <span
            class="emd-tip-move"
            *ngIf="row.movement"
            [class.is-up]="row.movement === 'up'"
            [class.is-down]="row.movement === 'down'"
          ></span>
        </div>
      </div>
    </div>
  `,
    styleUrls: ['./emd-chart-tooltip.component.scss'],
    standalone: false
})
export class EmdChartTooltipComponent {
  @Input() visible = false;
  @Input() title = '';
  @Input() rows: EmdTooltipRow[] = [];
  /** 'light' reproduces the shared donut's white card; others keep the dark chip. */
  @Input() variant: 'dark' | 'light' = 'dark';

  /** Pointer position within the chart host. */
  @Input() set x(value: number) { this.px = value; this.place(); }
  @Input() set y(value: number) { this.py = value; this.place(); }
  /** Host dimensions, so the tooltip knows which edge it is near. */
  @Input() hostWidth = 0;
  @Input() hostHeight = 0;

  @ViewChild('box') box?: ElementRef<HTMLDivElement>;

  left = 0;
  top = 0;

  private px = 0;
  private py = 0;

  private place(): void {
    const el = this.box?.nativeElement;
    const w = el?.offsetWidth || 160;
    const h = el?.offsetHeight || 60;
    // Resting offsets copied from the original charts (+15 / +5).
    const gapX = 15;
    const gapY = 5;

    // Flip to the other side of the cursor when the preferred side would spill.
    this.left = this.px + gapX + w > this.hostWidth ? this.px - gapX - w : this.px + gapX;
    this.top = this.py + gapY + h > this.hostHeight ? this.py - gapY - h : this.py + gapY;

    this.left = Math.max(4, this.left);
    this.top = Math.max(4, this.top);
  }
}
